import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check } from "lucide-react";
import { Logo } from "../brand/Logo";
import { subscribeNewsletter } from "../../lib/api";
import { toast } from "sonner";

const LEGAL_ROUTES = { terms: "/terms", privacy: "/privacy", acceptableUse: "/terms", pdpl: "/privacy" };
const COLUMNS = [
    { key: "products", items: ["domains", "dns", "email", "pricing"] },
    { key: "company", items: ["about", "careers", "contact", "partners"] },
    { key: "resources", items: ["docs", "status", "blog", "changelog"] },
    { key: "legal", items: ["terms", "privacy", "acceptableUse", "pdpl"] },
];

const PayBadge = ({ name, children }) => (
    <span
        className="inline-flex min-h-[40px] items-center rounded-[6px] border-2 border-white/30 bg-white/5 px-3 font-mono text-[12px] font-bold tracking-[0.16em] text-white transition-colors duration-200 hover:border-souq-400 hover:bg-white/10"
        aria-label={name}
        data-testid={`pay-${name.toLowerCase().replace(/\s/g, "")}`}
    >
        {children}
    </span>
);

export const Footer = () => {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await subscribeNewsletter(email, i18n.language);
            setSent(true);
            toast.success(t("footer.newsletter.success"));
            setEmail("");
        } catch (err) {
            toast.error("Could not subscribe — try again in a moment.");
        } finally { setLoading(false); }
    };

    return (
        <footer className="bg-redsea-grain text-white" data-testid="site-footer">
            {/* Asir stripe — top decoration */}
            <div className="asir-stripe h-2" aria-hidden="true" />

            <div className="mx-auto max-w-[1320px] px-5 pb-12 pt-20 lg:px-8 lg:pt-24">
                <div className="grid grid-cols-1 gap-14 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <Logo size={32} color="#FFFFFF" />
                        <p className="mt-6 max-w-sm text-[19px] font-medium leading-relaxed text-redsea-100">
                            {t("footer.tagline")}
                        </p>

                        <form onSubmit={handleSubscribe} className="mt-9 max-w-md" data-testid="footer-newsletter-form">
                            <label htmlFor="footer-newsletter" className="mb-2.5 block text-[13px] font-bold uppercase tracking-[0.18em] text-souq-400">
                                {t("footer.newsletter.title")}
                            </label>
                            <div className="flex items-center gap-2 rounded-[10px] border-2 border-white/30 bg-white/5 p-1.5 transition-colors duration-200 focus-within:border-souq-400">
                                <input
                                    id="footer-newsletter"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t("footer.newsletter.placeholder")}
                                    className="h-12 flex-1 bg-transparent px-3 text-[16px] font-medium text-white placeholder:text-redsea-200 focus:outline-none"
                                    data-testid="footer-newsletter-input"
                                />
                                <button
                                    type="submit"
                                    disabled={loading || sent}
                                    data-testid="footer-newsletter-submit"
                                    className="inline-flex h-12 items-center gap-2 rounded-[8px] bg-souq-400 px-5 text-[15px] font-bold text-ink-900 transition-colors duration-200 hover:bg-souq-300 disabled:opacity-60"
                                >
                                    {sent ? <Check className="h-4 w-4" /> : <ArrowRight className="h-4 w-4 rtl-mirror" />}
                                    {t("footer.newsletter.submit")}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="grid grid-cols-2 gap-10 md:col-span-7 md:grid-cols-4">
                        {COLUMNS.map((col) => (
                            <div key={col.key}>
                                <h4 className="mb-5 text-[13px] font-bold uppercase tracking-[0.18em] text-souq-400">{t(`footer.cols.${col.key}`)}</h4>
                                <ul className="space-y-3">
                                    {col.items.map((it) => (
                                        <li key={it}>
                                            <Link to={col.key === "legal" ? (LEGAL_ROUTES[it] || "#") : "#"} className="text-[16px] font-semibold text-redsea-100 transition-colors duration-200 hover:text-white" data-testid={`footer-link-${it}`}>
                                                {t(`footer.links.${it}`)}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Anthem */}
                <div className="mt-20 border-t-2 border-white/15 pt-10">
                    <p className="font-display text-[24px] sm:text-[28px] font-bold tracking-tight text-white">
                        {t("footer.anthem")} <span className="text-souq-400">·</span>{" "}
                        <span className="font-arabic text-souq-400" dir="rtl">{t("footer.anthemArabic")}</span>
                    </p>
                </div>

                {/* Compliance & payments */}
                <div className="mt-10 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
                    <div className="flex flex-wrap items-center gap-2">
                        <PayBadge name="Mada">MADA</PayBadge>
                        <PayBadge name="ApplePay">APPLE&nbsp;PAY</PayBadge>
                        <PayBadge name="Sadad">SADAD</PayBadge>
                        <PayBadge name="Visa">VISA</PayBadge>
                        <PayBadge name="Mastercard">MASTERCARD</PayBadge>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex min-h-[40px] items-center gap-1.5 rounded-[6px] border-2 border-white/30 bg-white/5 px-3 font-mono text-[12px] font-bold tracking-[0.14em] text-white">ZATCA</span>
                        <span className="inline-flex min-h-[40px] items-center gap-1.5 rounded-[6px] border-2 border-white/30 bg-white/5 px-3 font-mono text-[12px] font-bold tracking-[0.14em] text-white">PDPL</span>
                        <span className="inline-flex min-h-[40px] items-center gap-1.5 rounded-[6px] bg-souq-400 px-3 font-mono text-[12px] font-bold tracking-[0.14em] text-ink-900">SaudiNIC ACCREDITED</span>
                    </div>
                </div>

                <div className="mt-8 flex flex-col items-start justify-between gap-3 text-[14px] font-medium text-redsea-200 md:flex-row md:items-center">
                    <p>{t("footer.copyright", { year: new Date().getFullYear() })}</p>
                    <p className="font-mono num-ltr">{t("footer.icann")}</p>
                </div>
            </div>
        </footer>
    );
};
